/* eslint-disable react-hooks/exhaustive-deps */
// src/contexts/EventContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { ApiClient } from "../helpers/apiClient";
import { useMsal } from "@azure/msal-react";
import { TeamDTO } from "../types/teamMembers";
import { EventPhase } from "../enums/EventsPhaseEnum";

interface LocationDTO {
  id: number;
  name: string;
}

export interface EventDTO {
  id: string;
  eventID: string;
  name: string;
  enabled: boolean;
  locationName?: string;
  locationId?: number;
  allLocations?: LocationDTO[];
  status: number;
  phase: number;
  teams: TeamDTO[];
}

interface ApiEventDTO {
  id: string;
  name: string;
  enabled: boolean;
  locations?: LocationDTO[];
  status: number;
  phase: number;
  teams: TeamDTO[];
}

interface EventContextValue {
  events: EventDTO[];
  selectedEventId: string | null;
  selectedApiEventId: string | null;
  setSelectedEventId: (id: string | null) => void;
  loadAllEvents: () => Promise<void>;
}

const EventContext = createContext<EventContextValue>({
  events: [],
  selectedEventId: null,
  selectedApiEventId: null,
  setSelectedEventId: () => {},
  loadAllEvents: async () => {},
});

export const EventProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [events, setEvents] = useState<EventDTO[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { instance, accounts } = useMsal();

  /**
   * Flatten events so each location becomes its own row.
   */
  const flattenEvents = (apiEvents: ApiEventDTO[]): EventDTO[] =>
    apiEvents.flatMap((ev) => {
      const locations = ev.locations ?? [];

      if (locations.length >= 2 && ev.phase === EventPhase.Test) {
        return locations.map((loc) => ({
          id: `${ev.id}-${loc.id}`,
          eventID: ev.id,
          name: `${ev.name} (${loc.name})`,
          enabled: ev.enabled,
          locationName: loc.name,
          locationId: loc.id,
          allLocations: locations,
          status: ev.status,
          phase: ev.phase,
          teams: ev.teams,
        }));
      }

      // 0 or 1 location → keep single entry (append name if exists)
      const locSuffix = locations[0]?.name ? ` ${locations[0].name}` : "";
      return [
        {
          id: ev.id,
          eventID: ev.id,
          name:
            ev.phase === EventPhase.Test && locSuffix !== ""
              ? `${ev.name} (${locSuffix})`
              : `${ev.name}`,
          enabled: ev.enabled,
          status: ev.status,
          phase: ev.phase,
          teams: ev.teams,
          allLocations: locations,
        },
      ];
    });

  const loadAllEvents = async () => {
    const apiClient = ApiClient.getInstance(instance, accounts[0], {
      baseUrl: process.env.REACT_APP_API_BASE_URL,
    });

    const apiEvents: ApiEventDTO[] =
      (await apiClient?.get<ApiEventDTO[]>("Event/GetAll")) ?? [];
    const transformed = flattenEvents(apiEvents);
    setEvents(transformed);

    const defaultSelection = transformed.find((ev) => ev.enabled);
    if (defaultSelection) {
      setSelectedEventId(defaultSelection.id); // use uid for dropdown
    }
  };

  useEffect(() => {
    loadAllEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Derive the API id (eventID) from the uid currently selected.
   * Falls back to parsing the uid when the event list is still empty.
   */
  const selectedApiEventId = useMemo(() => {
    if (!selectedEventId) return null;
    const match = events.find((ev) => ev.id === selectedEventId);
    if (match) return match.eventID;
    return selectedEventId.split("-")[0] ?? null;
  }, [selectedEventId, events]);

  return (
    <EventContext.Provider
      value={{
        events,
        selectedEventId,
        selectedApiEventId,
        setSelectedEventId,
        loadAllEvents,
      }}
    >
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = () => useContext(EventContext);
